import classNames from 'classnames'
import { motion } from 'motion/react'
import React, { FC } from 'react'

import { Tooltip } from '@ui/components/Tooltip/Tooltip'

import useSensitiveDataFlag from '@utils/useSensitiveDataFlag'

import styles from './ProgressBar.module.scss'

export type Color = 'red' | 'blue' | 'purple' | 'grey' | 'green'
export type Size = 'small' | 'default'

export interface ProgressBarProps {
  color: Color
  percent: number | null
  tooltip?: string | null
  isCurrent?: boolean
  delay?: number
  duration?: number
  size?: Size
  stripesVariant?: StrokedBackgroundProps['variant']
  hasMinVisibleWidth?: boolean
}

export const PROGRESS_BAR_DURATION = 0.5

interface StrokedBackgroundProps {
  variant?: 'default' | 'primary' | 'info'
  stripeCount?: number
}

const StrokedBackground: React.FC<StrokedBackgroundProps> = ({ variant = 'default' }) => {
  return (
    <div className={classNames(styles.isStrokedBackground, styles[`stripe-variant-${variant}`])} />
  )
}

export const ProgressBar: FC<ProgressBarProps> = (props) => {
  const {
    percent,
    color,
    stripesVariant,
    tooltip,
    delay,
    duration = PROGRESS_BAR_DURATION,
    isCurrent,
    size = 'default',
    hasMinVisibleWidth,
  } = props
  const sensitiveDataFlag = useSensitiveDataFlag()
  const resolvedPercent = Math.max(0, percent ?? 0)

  return (
    <div className={classNames(styles.progressBar, styles[size])}>
      {(percent ?? 0) <= 0 && <div className={styles.isStrokedPlaceholder}></div>}

      <StrokedBackground variant={stripesVariant} />

      <Tooltip
        disabled={sensitiveDataFlag || (!isCurrent && resolvedPercent <= 0)}
        wrapperClassName={styles.tooltip}
        content={tooltip}
      >
        <motion.div
          className={classNames(styles.progressBarSection, styles[color], {
            [styles.hasMinVisibleWidth]: hasMinVisibleWidth && resolvedPercent > 0,
          })}
          initial={{ width: 0 }}
          animate={{ width: `${resolvedPercent}%` }}
          transition={{
            duration,
            ease: 'easeOut',
            delay: delay ?? 0,
          }}
        />
      </Tooltip>
    </div>
  )
}
